<template>
    <div class="rich-text-container" ref="editorContainer">
        <template v-if="!showEditor">
            <div class="loader-container">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
        </template>
        <textarea v-else ref="tinymceEditor"></textarea>
        <!-- Bind the dynamic ID -->

        <!-- Insert Image Modal -->
        <hb-modal title="Insert Image" v-model="show_add_image" size="medium" content-padding>
            <template v-slot:content>
                <hb-form label="Upload Image" required>
                    <HbFileInput v-model="image.src" v-validate="'required'" data-vv-scope="default"
                        data-vv-name="file_input" data-vv-as="Image File" :error="errors.has('default.file_input')" />
                </hb-form>
                <hb-form label="Alt Text">
                    <HbTextField v-model="image.alt" v-validate="'required|max:45|email'" data-vv-scope="default"
                        data-vv-name="alt_text" data-vv-as="Alt Text" :error="errors.has('default.alt_text')" />
                </hb-form>
            </template>
            <template v-slot:right-actions>
                <hb-btn color="primary" @click="insertImage">
                    Insert Image
                </hb-btn>
            </template>
        </hb-modal>

        <!-- Insert Link Modal -->
        <hb-modal title="Insert Link" v-model="show_add_link" size="medium" content-padding>
            <template v-slot:content>
                <hb-form label="URL" required>
                    <HbTextField v-model="link.url" v-validate="'required'" data-vv-scope="default" data-vv-name="url"
                        data-vv-as="URL" :error="errors.has('default.url')" />
                </hb-form>
                <hb-form label="Text">
                    <HbTextField v-model="link.text" v-validate="'required'" data-vv-scope="default" data-vv-name="text"
                        data-vv-as="Text" :error="errors.has('default.alt_text')" />
                </hb-form>
            </template>
            <template v-slot:right-actions>
                <hb-btn color="primary" @click="insertLink">
                    Insert Link
                </hb-btn>
            </template>
        </hb-modal>
    </div>
</template>

<script>
import { notificationMixin } from "@/mixins/notificationMixin.js";
import loadTinyMCE from '@/utils/tinymce-loader';
let tinymce
export default {
    name: 'MceRichTextEditor',
    mixins: [notificationMixin],
    props: {
        value: {
            type: String,
            default: ''
        },
        height: {
            type: Number,
            default: 200
        },
        tools: {
            type: Array,
            default: () => [
                'blocks',
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'alignleft',
                'aligncenter',
                'alignright',
                'insertImage',
                'insertLink',
                'bullist',
                'numlist',
                'blockquote',
                'removeformat',
                'lineheight',
            ]
        },
        menubar: {
            type: Boolean,
            default: false
        },
        statusbar: {
            type: Boolean,
            default: false
        },
        plugins: {
            type: Array,
            //Plugins passed should be passed as imports in script tag
            default: () => ['lists', 'code']
        },
        toolbar: {
            type: Boolean,
            default: true
        },
        excludeTools: {
            type: Array,
            //Plugins passed should be passed as imports in script tag
            default: () => []
        }
    },
    data() {
        return {
            editor: null,
            show_add_image: false,
            image: {
                src: '',
                alt_text: ''
            },
            show_add_link: false,
            link: {
                url: '',
                text: ''
            },
            showEditor: false
        };
    },
    watch: {
        value(newValue) {
            // Only update content if editor is ready and content is different
            if (this.editor && this.editor.getContent() !== newValue) {
                this.editor.setContent(newValue || '');
            }
        },
        height(newValue) {
            this.handleHeightChange(newValue);
        }
    },
    async mounted() {
        await this.loadEditor();
    },
    beforeDestroy() {
        this.destroyEditor();
    },
    methods: {
        async loadEditor() {
            try {
                const {
                    tinymceeditor,
                    tinymcePromise
                } = await loadTinyMCE();
                await tinymcePromise;
                tinymce = tinymceeditor;
                this.initializeTinyMce();
            } catch (error) {
                this.showMessageNotification({
                    type: 'error',
                    description: 'Some Error Occurred.'
                });
            }
        },
        async initializeTinyMce() {
            this.showEditor = true; // Trigger rendering of the <textarea>
            await this.$nextTick(); // Wait for DOM to update with textarea and ID
            tinymce.init({
                target: this.$refs.tinymceEditor,
                setup: (editor) => {
                    editor.on('init', () => {
                        this.editor = editor;
                        editor.setContent(this.value || '');
                        this.$nextTick(() => {
                            this.$emit('editorReady');
                            this.handleHeightChange(this.height);
                        })
                    });
                    editor.on('focus', () => {
                        this.handleEditorFocus();
                    });
                    editor.on('Change KeyUp Paste Undo Redo', () => {
                        const content = editor.getContent();
                        this.$emit('input', content);
                    });
                    editor.ui.registry.addButton('insertImage', {
                        icon: "image",
                        onAction: () => {
                            this.show_add_image = true;
                        }
                    });
                    editor.ui.registry.addButton('insertLink', {
                        icon: "link",
                        onAction: () => {
                            this.show_add_link = true;
                            const selectedText = editor.selection.getContent({
                                format: 'text'
                            });
                            this.link.text = selectedText || '';
                        }
                    });
                },
                apiKey: process.env.VUE_APP_TINYMCE_FREE_API_KEY, // Optional: replace with your TinyMCE API key
                license_key: 'gpl',
                toolbar_mode: 'sliding',
                ...this.editorConfig
            });
        },
        insertImage() {
            if (this.image.src.length === 0) {
                this.showMessageNotification({ type: 'error', description: "Image required. Please select one" });
                return;
            }

            const file = this.image.src[0];
            const reader = new FileReader();

            reader.onload = (event) => {
                const imgElement = document.createElement("img");
                imgElement.src = event.target.result;
                imgElement.classList.add('inserted-image');
                imgElement.classList.add('mceResizable');
                imgElement.setAttribute('data-mce-resize', 1);
                this.editor.insertContent(imgElement.outerHTML, {
                    select: true
                });
            };

            reader.readAsDataURL(file); // Convert to Base64

            this.image = {
                src: [],
                alt: ''
            };
            this.show_add_image = false;
        },
        closeModal() {
            this.show_add_link = false;
            this.link = {
                url: '',
                text: ''
            };
        },
        insertLink() {
            if (this.link.url.length === 0) {
                this.showMessageNotification({ type: 'error', description: "Link required. Please select one" });
                return;
            }
            let url = this.link.url.trim();
            let text = this.link.text.trim();
            if (!text) {
                text = url;
            }
            if (!/^https?:\/\//i.test(url)) {
                url = 'https://' + url;
            }
            this.editor.insertContent(`<a href="${url}" target="_blank" rel="noopener">${text}</a>`);
            this.closeModal();
        },
        handleEditorFocus() {
            this.$emit('focus');
        },
        getEditorContent() {
            if (this.editor) {
                const content = this.editor.getContent();
                return content;
            }
        },
        insertContent(value) {
            if (this.editor) {
                this.editor.insertContent(value);
            }
        },
        async destroyEditor() {
            if (this.editor) {
                this.showEditor = false;
                await this.editor.destroy();
                this.editor = null;
            } else return;
        },
        async handleHeightChange(newValue) {
            if (!this.editor) return;
            await this.$nextTick();
            const editorContainer = this.editor.getContainer();
            if (editorContainer)
                editorContainer.style.height = `${newValue}px`;
        }
    },
    computed: {
        editorConfig() {
            return {
                statusbar: this.statusbar,
                plugins: this.plugins,
                toolbar: this.toolbar ? this.filteredToolbar.join(' | ') : false,
                menubar: this.menubar,
                resize: false,
                auto_resize: false,
                block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6;',
                content_style: 'p { margin-block-start: 0px !important; margin-block-end: 0px !important;} ' + '.mceEditable { border: 2px solid #02AD0F;transition: border-color 0.2s ease-in-out; }',
                highlight_on_focus: false,
                noneditable_noneditable_class: 'mceNonEditable',
                editable_class: 'mceEditable',
                browser_spellcheck: true
            }
        },
        filteredToolbar() {
            const tools = this.tools || [];
            const exclude = this.excludeTools || [];

            if (!exclude.length) return tools;

            return tools.filter(item => !exclude.includes(item));
        }
    }
};
</script>

<style scoped>
::v-deep(.tox-toolbar__group) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-right: 0 !important;
}

::v-deep(.tox-toolbar__group:first-child) {
    padding-left: 8px !important;
}

.rich-text-container {
    width: 100%;
}

.loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
    /* or whatever space you want */
}

.tox .tox-tinymce {
    height: var(--height) !important;
}
</style>
